import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, INVESTMENT_TYPES } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, Home, History, Building2, Scale, AlertTriangle, RefreshCw, RefreshCcw, Clock, ZapOff, Hammer, Trophy } from "lucide-react";
import InvestmentFormDialog from "@/components/InvestmentFormDialog";
import ValuationHistoryDialog from "@/components/ValuationHistoryDialog";
import ComparePropertiesDialog from "@/components/ComparePropertiesDialog";
import PriceSparkline from "@/components/PriceSparkline";
import PriceHistoryDialog from "@/components/PriceHistoryDialog";
import RenovationsDialog from "@/components/RenovationsDialog";
import InvestmentTimelineDialog from "@/components/InvestmentTimelineDialog";
import DepositRenewalDialog from "@/components/DepositRenewalDialog";
import ReturnsRankingDialog from "@/components/ReturnsRankingDialog";
import { formatRelativeShortEs } from "@/lib/relativeTime";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const typeLabel = (t) => INVESTMENT_TYPES.find((i) => i.value === t)?.label || t;
const PROP_LABELS = { piso: "Piso", casa: "Casa", local: "Local", garaje: "Garaje", terreno: "Terreno", otro: "Otro" };

// LTV badge color: verde < 60, ámbar 60-80, rojo > 80
const ltvTone = (pct) => {
  if (pct == null) return null;
  if (pct < 60) return { bg: "bg-[#1C7C54]/10", txt: "text-[#1C7C54]", label: "LTV bajo" };
  if (pct <= 80) return { bg: "bg-[#C89A3F]/10", txt: "text-[#8B6B1F]", label: "LTV medio" };
  return { bg: "bg-[#D95D39]/10", txt: "text-[#D95D39]", label: "LTV alto" };
};

export default function Investments() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);
  const [debts, setDebts] = useState([]);
  const [reAnalytics, setReAnalytics] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [renovationsProperty, setRenovationsProperty] = useState(null);
  const [renovationsOpen, setRenovationsOpen] = useState(false);
  const [timelineInvestment, setTimelineInvestment] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [renewDeposit, setRenewDeposit] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);
  const [refreshingRow, setRefreshingRow] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false);
  const [priceHistoryItem, setPriceHistoryItem] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      const [inv, dbts, re] = await Promise.all([
        api.get("/investments"),
        api.get("/debts"),
        api.get("/analytics/real-estate").catch(() => ({ data: { available: false } })),
      ]);
      setItems(inv.data);
      setDebts(dbts.data || []);
      setReAnalytics(re.data?.available ? re.data : null);
    } catch (e) {
      logger.error("investments load", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Background refresh if the last full refresh was >12h ago. Silent (no toast
  // unless something new appears). Runs once per mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.get("/investments/refresh-status");
        if (cancelled || !status.data?.stale) return;
        const res = await api.post("/investments/refresh-all");
        if (cancelled) return;
        const total = res.data?.total_updated ?? 0;
        if (total > 0) {
          load();
        }
      } catch (e) {
        logger.warn("silent refresh-all", e);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const handleNew = () => { setEditing(null); setDialogOpen(true); };
  const handleEdit = (it) => { setEditing(it); setDialogOpen(true); };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/investments/${id}`);
      toast.success("Inversión eliminada");
      load();
    } catch { toast.error("No se pudo eliminar"); }
  };

  const refreshRow = async (it) => {
    setRefreshingRow(it.id);
    try {
      const res = await api.post(`/investments/${it.id}/refresh`);
      if (res.data?.updated) {
        toast.success(`Precio actualizado: ${res.data.current_price} ${it.currency}`);
        load();
      } else {
        toast.warning("No se pudo obtener un precio actual");
      }
    } catch (e) {
      logger.error("refresh single", e);
      toast.error("Error refrescando el precio");
    } finally {
      setRefreshingRow(null);
    }
  };

  const refreshAll = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.post("/investments/refresh-all");
      const total = res.data?.total_updated ?? 0;
      const parts = [];
      if (res.data?.crypto_updated) parts.push(`${res.data.crypto_updated} cripto`);
      if (res.data?.stock_updated) parts.push(`${res.data.stock_updated} acciones`);
      if (res.data?.isin_updated) parts.push(`${res.data.isin_updated} ISIN`);
      if (total > 0) toast.success(`Precios actualizados · ${parts.join(" · ")}`);
      else toast.info("No había cambios que aplicar");
      const unresolved = res.data?.isin_unresolved || [];
      if (unresolved.length) toast.warning(`Sin resolver: ${unresolved.join(", ")}`);
      if (res.data?.stock_error === "rate_limited") {
        toast.warning("Límite de Alpha Vantage alcanzado");
      }
      load();
    } catch (e) {
      logger.error("refresh all", e);
      toast.error("No se pudieron actualizar los precios");
    } finally {
      setRefreshingAll(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex items-end justify-between gap-4 fade-up">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2">{t("investmentsPage.kicker")}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#0A110E]">{t("investmentsPage.title")}</h1>
          <p className="text-[#5F7368] mt-2">Registra y gestiona tus activos.</p>
        </div>
        <div className="flex items-center gap-2">
          {(items.some((i) => i.type === "stock") ||
            items.some((i) => (i.isin || "").length === 12) ||
            items.some((i) => i.type === "crypto")) && (
            <Button
              data-testid="refresh-all-btn"
              variant="outline"
              onClick={refreshAll}
              disabled={refreshingAll}
              className="rounded-xl border-[#EBEBEA]"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingAll ? "animate-spin" : ""}`} />
              Actualizar todos los precios
            </Button>
          )}
          {items.filter((i) => !i.matured && i.type !== "real_estate").length >= 2 && (
            <Button
              data-testid="compare-returns-btn"
              variant="outline"
              onClick={() => setRankingOpen(true)}
              className="rounded-xl border-[#8B6B1F]/40 text-[#8B6B1F] hover:bg-[#C89A3F]/5"
            >
              <Trophy className="w-4 h-4 mr-2" /> Comparar rentabilidades
            </Button>
          )}
          {items.filter((i) => i.type === "real_estate").length >= 2 && (
            <Button
              data-testid="compare-properties-btn"
              variant="outline"
              onClick={() => setCompareOpen(true)}
              className="rounded-xl border-[#1C7C54]/40 text-[#1C7C54] hover:bg-[#1C7C54]/5"
            >
              <Scale className="w-4 h-4 mr-2" /> Comparar inmuebles
            </Button>
          )}
          <Button data-testid="add-investment-btn" onClick={handleNew} className="bg-[#0F291E] hover:bg-[#1C7C54] rounded-xl">
            <Plus className="w-4 h-4 mr-2" /> Nueva inversión
          </Button>
        </div>
      </div>

      <div className="card-surface overflow-hidden">
        {(() => {
          if (loading) return <div className="p-8 text-[#5F7368]">Cargando…</div>;
          if (items.length === 0) {
            return (
              <div className="p-16 text-center">
                <p className="text-[#8A9992] mb-4">Aún no tienes inversiones registradas.</p>
                <Button onClick={handleNew} className="bg-[#0F291E] hover:bg-[#1C7C54]">
                  <Plus className="w-4 h-4 mr-2" /> Agrega tu primera inversión
                </Button>
              </div>
            );
          }
          return (
          <div className="divide-y divide-[#EBEBEA]">
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">
              <div className="col-span-2">Nombre</div>
              <div className="col-span-2">Tipo</div>
              <div className="col-span-1 text-right">Cantidad</div>
              <div className="col-span-2 text-right">Precio compra</div>
              <div className="col-span-2 text-right">Valor actual</div>
              <div className="col-span-1 text-right">P/L</div>
              <div className="col-span-2"></div>
            </div>
            {items.filter((it) => !it.matured).map((it) => {
              const cost = (it.purchase_price || 0) * (it.quantity || 0);
              const value = ((it.current_price ?? it.purchase_price) || 0) * (it.quantity || 0);
              const pl = value - cost;
              const positive = pl >= 0;
              const isRE = it.type === "real_estate";
              const mortgage = isRE && it.linked_mortgage_id
                ? debts.find((d) => d.id === it.linked_mortgage_id)
                : null;
              const mortgageRemaining = mortgage ? Number(mortgage.remaining_amount || 0) : 0;
              const equity = isRE ? value - mortgageRemaining : null;
              const grossYield = isRE && cost > 0 && it.rental_income
                ? ((Number(it.rental_income) * 12) / cost) * 100
                : null;
              const reMeta = isRE && reAnalytics
                ? (reAnalytics.items || []).find((x) => x.id === it.id)
                : null;
              const netYield = reMeta?.net_yield;
              const revaluationAlert = reMeta?.revaluation_alert;
              const revaluationPct = reMeta?.revaluation_pct;
              const ltvPct = reMeta?.ltv_pct;
              const ltv = mortgage ? ltvTone(ltvPct) : null;
              return (
                <div key={it.id} data-testid={`investment-row-${it.id}`} className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-[#FAFAF9] transition-colors">
                  <div className="col-span-12 md:col-span-2">
                    <div className="font-medium text-[#0A110E] flex items-center gap-2 flex-wrap">
                      {isRE && <Home className="w-3.5 h-3.5 text-[#1C7C54]" />}
                      {it.name}
                      {revaluationAlert && (
                        <span
                          data-testid={`revaluation-alert-${it.id}`}
                          title={`Revalorización de ${revaluationPct > 0 ? "+" : ""}${revaluationPct}% respecto a la tasación anterior`}
                          className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            revaluationPct >= 0
                              ? "bg-[#1C7C54]/10 text-[#1C7C54]"
                              : "bg-[#D95D39]/10 text-[#D95D39]"
                          }`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {revaluationPct > 0 ? "+" : ""}{revaluationPct}%
                        </span>
                      )}
                    </div>
                    {it.isin && (
                      <div
                        data-testid={`inv-isin-${it.id}`}
                        title={it.yahoo_symbol ? `Yahoo: ${it.yahoo_symbol}` : "Aún sin símbolo Yahoo resuelto"}
                        className="text-[10px] text-[#8A9992] tabular-nums tracking-wider mt-0.5"
                      >
                        {it.isin}
                      </div>
                    )}
                    {isRE ? (
                      <div className="text-xs text-[#8A9992] flex items-center gap-2 mt-0.5 flex-wrap">
                        {it.property_type && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#1C7C54]/10 text-[#1C7C54]">
                            {PROP_LABELS[it.property_type] || it.property_type}
                          </span>
                        )}
                        {it.address && <span className="truncate">{it.address}</span>}
                      </div>
                    ) : (
                      it.ticker && <div className="text-xs text-[#8A9992] uppercase">{it.ticker}</div>
                    )}
                    {isRE && mortgage && (
                      <div className="text-[11px] text-[#5F7368] mt-1 flex items-center gap-1 flex-wrap">
                        <Building2 className="w-3 h-3 text-[#D95D39]" />
                        <span>Hipoteca: {mortgage.creditor}</span>
                        <span className="tabular-nums">−{formatCurrency(mortgageRemaining, it.currency)}</span>
                        {ltv && ltvPct != null && (
                          <span
                            data-testid={`ltv-badge-${it.id}`}
                            title={`Loan-to-Value: hipoteca / valor. ${ltv.label}.`}
                            className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${ltv.bg} ${ltv.txt}`}
                          >
                            LTV {ltvPct}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-2 text-sm text-[#5F7368]">
                    <div>{typeLabel(it.type)}</div>
                    {it.type === "deposit" && it.interest_rate != null && (
                      <div
                        data-testid={`interest-rate-${it.id}`}
                        className="text-xs text-[#1C7C54] tabular-nums"
                        title="Nominal annual interest rate"
                      >
                        {Number(it.interest_rate).toFixed(2)}% APR
                      </div>
                    )}
                    {it.type === "deposit" && it.accrued_interest != null && (
                      <div
                        data-testid={`accrued-${it.id}`}
                        className="text-xs text-[#1C7C54] tabular-nums"
                        title="Accrued interest since start date · not counted in net worth"
                      >
                        + {formatCurrency(it.accrued_interest, it.currency)}
                        <span className="text-[#8A9992]"> · {it.accrued_days}d</span>
                      </div>
                    )}
                    {it.type === "deposit" && it.days_to_maturity != null && (() => {
                      const d = it.days_to_maturity;
                      let label;
                      let cls = "text-[#8A9992]";
                      if (d < 0) { label = t("investmentsPage.maturedAgo", { count: -d, defaultValue: "Matured {{count}}d ago" }); cls = "text-[#D95D39] font-semibold"; }
                      else if (d === 0) { label = t("investmentsPage.maturesToday", { defaultValue: "Matures today" }); cls = "text-[#D95D39] font-semibold"; }
                      else if (d <= 30) { label = t("investmentsPage.maturesIn", { count: d, defaultValue: "Matures in {{count}}d" }); cls = "text-[#8B6B1F] font-semibold"; }
                      else { label = t("investmentsPage.maturesIn", { count: d, defaultValue: "Matures in {{count}}d" }); }
                      return (
                        <div
                          data-testid={`maturity-${it.id}`}
                          className={`text-xs tabular-nums ${cls}`}
                          title={`Maturity date: ${it.maturity_date}`}
                        >
                          {label}
                        </div>
                      );
                    })()}
                    {isRE && it.rental_income ? (
                      <div className="text-xs text-[#1C7C54] tabular-nums">
                        {formatCurrency(it.rental_income, it.currency)}/mes
                        {grossYield !== null && (
                          <span className="text-[#5F7368]"> · {grossYield.toFixed(2)}% bruto</span>
                        )}
                        {netYield !== undefined && netYield !== null && (
                          <span
                            data-testid={`net-yield-${it.id}`}
                            className={`ml-1 ${netYield >= 0 ? "text-[#1C7C54]" : "text-[#D95D39]"}`}
                            title="Rentabilidad neta = alquiler anual − IBI − IRPF − cuota hipoteca anual"
                          >
                            · {netYield.toFixed(2)}% neto
                          </span>
                        )}
                      </div>
                    ) : null}
                  </div>
                  <div className="col-span-6 md:col-span-1 md:text-right text-sm tabular-nums">{Number(it.quantity).toLocaleString()}</div>
                  <div className="col-span-6 md:col-span-2 md:text-right text-sm tabular-nums text-[#5F7368]">{formatCurrency(it.purchase_price, it.currency)}</div>
                  <div className="col-span-6 md:col-span-2 md:text-right text-sm font-semibold tabular-nums">
                    <div className="flex items-center justify-end gap-2">
                      {!isRE && (
                        <button
                          type="button"
                          data-testid={`open-history-${it.id}`}
                          title="Ver histórico completo"
                          onClick={() => { setPriceHistoryItem(it); setPriceHistoryOpen(true); }}
                          className="rounded hover:bg-[#EBEBEA]/60 p-0.5"
                        >
                          <PriceSparkline
                            investmentId={it.id}
                            days={30}
                            positive={pl >= 0}
                          />
                        </button>
                      )}
                      <span>{formatCurrency(value, it.currency)}</span>
                    </div>
                    {!isRE && it.price_updated_at && (
                      <div
                        data-testid={`price-fresh-${it.id}`}
                        className={`flex items-center justify-end gap-1 text-[10px] font-normal mt-0.5 ${
                          it.is_stale ? "text-[#D95D39]" : "text-[#8A9992]"
                        }`}
                      >
                        {it.is_stale ? (
                          <ZapOff className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                        <span title={it.price_source ? `Fuente: ${it.price_source}` : ""}>
                          {formatRelativeShortEs(it.price_updated_at)}
                        </span>
                      </div>
                    )}
                    {!isRE && it.is_stale && it.isin && !it.price_updated_at && (
                      <div
                        data-testid={`price-unresolved-${it.id}`}
                        className="flex items-center justify-end gap-1 text-[10px] text-[#D95D39] mt-0.5"
                        title="No se ha podido obtener precio para este ISIN"
                      >
                        <ZapOff className="w-3 h-3" />
                        <span>ISIN sin cotización</span>
                      </div>
                    )}
                    {isRE && mortgage && (
                      <div className="text-[11px] font-medium text-[#1C7C54] tabular-nums">
                        equity {formatCurrency(equity, it.currency)}
                      </div>
                    )}
                  </div>
                  <div className={`col-span-6 md:col-span-1 md:text-right text-sm tabular-nums font-medium flex md:justify-end items-center gap-1 ${positive ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
                    {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span className="truncate">{formatCurrency(Math.abs(pl), it.currency)}</span>
                  </div>
                  <div className="col-span-12 md:col-span-2 flex justify-end gap-1">
                    {(it.isin || (it.type === "stock" && it.ticker) || it.type === "crypto") && (
                      <button
                        onClick={() => refreshRow(it)}
                        disabled={refreshingRow === it.id}
                        data-testid={`refresh-inv-${it.id}`}
                        title="Actualizar precio ahora"
                        className="p-2 rounded-lg hover:bg-[#EBEBEA] disabled:opacity-40"
                      >
                        <RefreshCw className={`w-4 h-4 text-[#1C7C54] ${refreshingRow === it.id ? "animate-spin" : ""}`} />
                      </button>
                    )}
                    {isRE && (
                      <button
                        onClick={() => { setHistoryItem(it); setHistoryOpen(true); }}
                        data-testid={`history-inv-${it.id}`}
                        title="Historial de tasaciones"
                        className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      >
                        <History className="w-4 h-4 text-[#5F7368]" />
                      </button>
                    )}
                    {isRE && (
                      <button
                        onClick={() => { setRenovationsProperty(it); setRenovationsOpen(true); }}
                        data-testid={`renov-inv-${it.id}`}
                        title="Reformas"
                        className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      >
                        <Hammer className="w-4 h-4 text-[#8B6B1F]" />
                      </button>
                    )}
                    {!isRE && (
                      <button
                        onClick={() => { setTimelineInvestment(it); setTimelineOpen(true); }}
                        data-testid={`timeline-inv-${it.id}`}
                        title="Timeline de movimientos vinculados"
                        className="p-2 rounded-lg hover:bg-[#EBEBEA]"
                      >
                        <History className="w-4 h-4 text-[#1C7C54]" />
                      </button>
                    )}
                    {it.type === "deposit" && !it.matured && typeof it.days_to_maturity === "number" && it.days_to_maturity <= 7 && (
                      <button
                        onClick={() => { setRenewDeposit(it); setRenewOpen(true); }}
                        data-testid={`renew-inv-${it.id}`}
                        title={it.days_to_maturity < 0 ? "Depósito vencido — renovar" : `Vence en ${it.days_to_maturity}d — renovar`}
                        className={`p-2 rounded-lg hover:bg-[#EBEBEA] ${it.days_to_maturity < 0 ? "bg-[#D95D39]/10" : "bg-[#C89A3F]/10"}`}
                      >
                        <RefreshCcw className={`w-4 h-4 ${it.days_to_maturity < 0 ? "text-[#D95D39]" : "text-[#8B6B1F]"}`} />
                      </button>
                    )}
                    <button onClick={() => handleEdit(it)} data-testid={`edit-inv-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Pencil className="w-4 h-4 text-[#5F7368]" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button data-testid={`delete-inv-${it.id}`} className="p-2 rounded-lg hover:bg-[#EBEBEA]"><Trash2 className="w-4 h-4 text-[#D95D39]" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar inversión?</AlertDialogTitle>
                          <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(it.id)} className="bg-[#D95D39] hover:bg-[#D95D39]/90">Eliminar</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      <InvestmentFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={load}
      />
      <ValuationHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        investment={historyItem}
        onChanged={load}
      />
      <ComparePropertiesDialog open={compareOpen} onOpenChange={setCompareOpen} />

      <PriceHistoryDialog
        open={priceHistoryOpen}
        onOpenChange={setPriceHistoryOpen}
        investment={priceHistoryItem}
        baseCurrency={user?.base_currency}
        allInvestments={items}
      />
      <RenovationsDialog
        open={renovationsOpen}
        onOpenChange={setRenovationsOpen}
        property={renovationsProperty}
      />
      <InvestmentTimelineDialog
        open={timelineOpen}
        onOpenChange={setTimelineOpen}
        investment={timelineInvestment}
      />
      <DepositRenewalDialog
        open={renewOpen}
        onOpenChange={setRenewOpen}
        deposit={renewDeposit}
        onRenewed={load}
      />
      <ReturnsRankingDialog
        open={rankingOpen}
        onOpenChange={setRankingOpen}
      />
    </div>
  );
}
